import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { mergeItems, websiteItem } from "./intakeModel";

/* mode "commit": saves to the CRM. mode "stage": hands reviewed items back via onStage (onboarding). */
export const useIntake = ({ userId, instanceId, mode = "commit", initialItems = [], seedCustomers = [], onStage, onCommitted }) => {
  const [tab, setTab] = useState("upload");
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [defaultRel, setDefaultRel] = useState("auto");
  const [items, setItems] = useState(initialItems);
  const [webItems, setWebItems] = useState(seedCustomers.map(websiteItem));
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");

  useEffect(() => { if (mode === "stage") onStage?.(items); }, [items, mode, onStage]);

  useEffect(() => {
    if (mode !== "commit" || !userId) return;
    axios.get(`${API}/intake/website-customers/${userId}`).then(({ data }) => setWebItems(data.items || [])).catch(() => {});
  }, [mode, userId]);

  const addFiles = useCallback((list) => setFiles((prev) => {
    const names = new Set(prev.map((f) => f.name));
    return [...prev, ...Array.from(list).filter((f) => !names.has(f.name))];
  }), []);
  const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.name !== name));

  const analyze = async () => {
    if (!files.length && !text.trim()) { toast.error("Add a file or paste some text first"); return; }
    setBusy(true);
    setStage(files.length ? "Reading your files" : "Reading your notes");
    try {
      const form = new FormData();
      form.append("userId", userId);
      form.append("defaultRelationship", defaultRel);
      if (text.trim()) form.append("text", text.trim());
      files.forEach((f) => form.append("files", f));
      const { data } = await axios.post(`${API}/intake/analyze`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      (data.warnings || []).forEach((w) => toast.warning(w));
      if (!data.items?.length) { toast.error("Nothing recognisable was found — try a clearer list or a spreadsheet with headers"); return; }
      setItems((prev) => mergeItems(prev, data.items));
      setFiles([]); setText("");
      toast.success(`Found ${data.summary.accounts} companies and ${data.summary.contacts} people — review below`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that input");
    } finally { setBusy(false); setStage(""); }
  };

  const addWebsiteItems = (selected) => {
    setItems((prev) => mergeItems(prev, selected));
    setWebItems((prev) => prev.filter((w) => !selected.some((s) => s.id === w.id)));
  };

  const setRelationship = (id, relationship) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, relationship } : i)));
  const setAllRelationship = (relationship) => setItems((prev) => prev.map((i) => ({ ...i, relationship })));
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clear = () => setItems([]);

  const commit = async () => {
    if (!items.length) return null;
    setBusy(true);
    setStage("Saving");
    try {
      const { data } = await axios.post(`${API}/intake/commit`, { userId, instanceId: instanceId || null, items });
      toast.success(`Saved ${data.accountsCreated + data.accountsUpdated} accounts and ${data.contactsCreated + data.contactsUpdated} contacts`);
      setItems([]);
      onCommitted?.(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
      return null;
    } finally { setBusy(false); setStage(""); }
  };

  return { tab, setTab, files, addFiles, removeFile, text, setText, defaultRel, setDefaultRel, items, webItems, busy, stage,
    analyze, addWebsiteItems, setRelationship, setAllRelationship, removeItem, clear, commit };
};
